import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useSearchUnassignedRCsLazyQuery } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { useGetUser } from 'v2/core/services/user';

interface SearchUnassignedRCsInput {
  searchTerm: string;
}

export function useSearchUnassignedRCs(
  { searchTerm }: SearchUnassignedRCsInput,
  shouldSkipNetworkCall?: boolean,
) {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  const [getUnassignedRCs, resp] = useSearchUnassignedRCsLazyQuery({
    variables: {
      request: {
        tenantID,
        query: debouncedSearchTerm,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  useEffect(() => {
    if (!debouncedSearchTerm || shouldSkipNetworkCall) {
      return;
    }

    getUnassignedRCs({
      variables: {
        request: {
          tenantID,
          query: debouncedSearchTerm,
        },
      },
      onError() {
        showErrorNotification({
          contentComponent: 'Failed to query Definitions',
          props: {},
        });
      },
    });
  }, [
    debouncedSearchTerm,
    getUnassignedRCs,
    shouldSkipNetworkCall,
    showErrorNotification,
    tenantID,
  ]);

  return {
    ...resp,
    debouncedSearchTerm,
    loading: resp.data ? false : resp.loading,
  };
}
